import { CampaignState, CampaignType } from 'modules/domains/campaign';
import {
  CampaignStateDefinition,
  ExperimentState,
  ExperimentType,
  MessageMedium,
} from 'modules/graphql';
import { WinningCriteriaTypeDefinition } from 'modules/graphql/types';
import { colors } from 'ui/theme';
import { ExperimentTakeoverSource } from '../constants';
import {
  CampaignInfoState,
  ExperimentTakeoverMetaData,
  ExperimentTakeoverState,
  GetExperimentLinkAttributes,
  MutatedEditExperimentState,
  OriginalExperimentState,
  TypeCampaign,
} from '../types';

export const mockExperimentTakeoverMetaPayload: ExperimentTakeoverMetaData = {
  source: ExperimentTakeoverSource.CAMPAIGN_EDITOR,
};

export const mockCampaignInfoPayload: CampaignInfoState = {
  id: 0,
  name: 'Super Campaign',
  campaignState: CampaignStateDefinition.Running,
  type: CampaignType.Blast,
  isCampaignSTOEnabled: false,
  isCampaignValid: true,
  hasLocales: false,
};

export const mockLinkAttributeWithNoExperiment: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.CAMPAIGN_EDITOR,
  experiment: null,
};

export const mockLinkAttributeWithNoCampaign: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.EXPERIMENT_ACTION_MENU_INDEX,
  experiment: {
    id: 1,
    state: ExperimentState.RUNNING,
  },
  campaign: {} as TypeCampaign,
};

export const mockRunningExperimentLinkInsideExperimentModule: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.EXPERIMENT_ACTION_MENU_INDEX,
  experiment: {
    id: 1,
    state: ExperimentState.RUNNING,
  },
  campaign: {
    id: 2,
    type: CampaignType.Blast,
  } as TypeCampaign,
};

export const mockRunningExperimentNameLinkInsideExperimentModule: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.EXPERIMENT_NAME_INDEX,
  experiment: {
    id: 1,
    state: ExperimentState.RUNNING,
  },
  campaign: {
    id: 2,
    type: CampaignType.Blast,
  } as TypeCampaign,
};

export const mockDraftExperimentLinkOutsideExperimentModule: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.CAMPAIGN_INDEX,
  experiment: {
    id: 1,
    state: ExperimentState.DRAFT,
  },
  campaign: {
    id: 2,
    type: CampaignType.ApiTriggered,
  } as TypeCampaign,
};

export const mockFinishedExperimentLinkOutsideExperimentModule: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.CAMPAIGN_INDEX,
  experiment: {
    id: 1,
    state: ExperimentState.FINISHED,
  },
  campaign: {
    id: 2,
    type: CampaignType.WorkflowTriggered,
  } as TypeCampaign,
};

export const mockDraftExperimentLinkInsideCampaignEditor: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.CAMPAIGN_EDITOR,
  experiment: {
    id: 1,
    state: ExperimentState.DRAFT,
  },
};

export const mockFinishedExperimentLinkInsideWorkflowEditor: GetExperimentLinkAttributes = {
  source: ExperimentTakeoverSource.WORKFLOW_NODE_EDITOR,
  experiment: {
    id: 1,
    state: ExperimentState.FINISHED,
  },
};

export const mockOriginalEditExperimentPayload: OriginalExperimentState = {
  id: 1,
  name: 'Super Campaign Experiment',
  type: ExperimentType.SubjectLine,
  state: ExperimentState.DRAFT,
  campaign: {
    id: 1,
    name: 'Super Campaign',
    type: CampaignType.Blast,
    state: CampaignState.Running,
    messageMedium: MessageMedium.Email,
    sendTimeUseRecipientTimeZones: false,
    conversions: [],
    lists: [],
  },
  evenlySplitVariations: false,
  winningCriteria: {
    name: 'Test Winning Criteria',
    type: WinningCriteriaTypeDefinition.ConversionEvent,
  },
  attributionPeriodHours: 0,
  testSizePercentage: 0,
  testSendsPerVariation: 1000,
  testDurationInMinutes: 25,
  holdout: null,
  variations: [
    {
      id: 1,
      name: 'Variation 1',
      control: false,
      sendTime: '2023-08-08T12:00:00-04:00[America/New_York]',
      content: {
        subject: 'This is another subject line',
        preheaderText: "Here's some preheader text",
        fromName: 'Experimentation - Test Project',
        fromEmail: 'testm@iterable.com',
      },
    },
    {
      id: 2,
      name: 'Base Template (Email - Multi Locale)',
      control: true,
      sendTime: null,
      content: {
        subject: 'This is the subject line',
        preheaderText: "Here's some preheader text",
        fromName: 'Experimentation - Test Project',
        fromEmail: 'testm@iterable.com',
      },
    },
  ],
};

export const mockMutatedEditExperimentPayload: MutatedEditExperimentState = {
  ...mockOriginalEditExperimentPayload,
  // Control variant is sorted first and we add the alphabets
  variations: [
    {
      ...mockOriginalEditExperimentPayload.variations[1],
      alphabet: 'A',
      bgColor: colors.purple10,
      textColor: colors.purple100,
      borderColor: colors.purple20,
    },
    {
      ...mockOriginalEditExperimentPayload.variations[0],
      alphabet: 'B',
      bgColor: colors.green10,
      textColor: colors.green100,
      borderColor: colors.green20,
    },
  ],
};

export const mockExperimentTakeoverState: ExperimentTakeoverState = {
  isDrawerOpen: false,
  isModalOpen: false,
  source: ExperimentTakeoverSource.CAMPAIGN_EDITOR,
};

/**
 * Shared with external modules
 */
export const mockExperimentTakeoverReduxState = {
  experiments: {
    takeover: {
      info: mockExperimentTakeoverState,
      campaignInfo: mockCampaignInfoPayload,
      experiment: {
        currentLocale: '',
      },
    },
  },
};
